import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import Dialog from "@mui/material/Dialog";
import { Box, Chip, Paper } from "@mui/material";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CommunityApi from "../../shared/api/CommunityApi";
import { GalleryBand, GalleryDrawing } from "../../shared/interfaces/GameLogicTypes";
import { Languages } from "../../shared/language/languages";
import "./GalleryBands.css";

const BANDS_PER_PAGE = 30;
const DRAWINGS_PER_BATCH = 6;
// stand-in names for {{tsar}} (prompts are shown outside a game)
const NAMES = ["Alex", "Sam", "Jordan", "Taylor", "Morgan", "Jamie", "Riley", "Casey", "Robin", "Noa"];

const LANGUAGE_LIST = Object.entries(Languages).map(([label, lang]) => ({ ...lang, label: label.charAt(0).toUpperCase() + label.slice(1) }));
const LANGUAGE_IDS = new Set(LANGUAGE_LIST.map((l) => l.id));
// browser language code → our language id where they differ
const BROWSER_TO_ID: Record<string, string> = { ja: "jp", zh: "cn", ko: "kr", da: "dn", sv: "se", hi: "in", ar: "sa", tl: "ph", fil: "ph" };

/** English plus the visitor's preferred browser language (when we have it). */
function defaultLanguages(): string[] {
  const langs = ["en"];
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = (tag || "").toLowerCase().split("-")[0];
    const id = BROWSER_TO_ID[base] ?? base;
    if (LANGUAGE_IDS.has(id)) {
      if (!langs.includes(id)) langs.push(id);
      break;
    }
  }
  return langs;
}

const newSeed = () => 1 + Math.floor(Math.random() * 2_000_000_000);

function getVisitorId(): string {
  const KEY = "galleryVisitorId";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

function getReportedIds(): Set<number> {
  try {
    return new Set(JSON.parse(localStorage.getItem("galleryReported") || "[]"));
  } catch {
    return new Set();
  }
}

/** <h>…</h> → highlighted segments, {{tsar}} → a name. */
function promptSegments(prompt: string, seed: number) {
  let n = seed;
  return prompt
    .replace(/\{\{\s*(tsar|friend)\s*\}\}/gi, () => NAMES[n++ % NAMES.length])
    .split(/(<h>[\s\S]*?<\/h>)/)
    .filter(Boolean)
    .map((part) => {
      const m = part.match(/^<h>([\s\S]*)<\/h>$/);
      return { text: (m ? m[1] : part).replace(/<[^>]+>/g, ""), highlight: !!m };
    });
}

function Prompt({ prompt, seed }: { prompt: string; seed: number }) {
  const segments = useMemo(() => promptSegments(prompt, seed), [prompt, seed]);
  return (
    <>
      {segments.map((s, i) => (s.highlight ? <mark key={i}>{s.text}</mark> : <React.Fragment key={i}>{s.text}</React.Fragment>))}
    </>
  );
}

type Vote = "like" | "dislike" | "report";
type VoteFn = (d: GalleryDrawing, action: Vote) => Promise<GalleryDrawing | null>;

interface SpotlightState {
  band: GalleryBand;
  bandsByDrawing?: Record<number, GalleryBand>;
  drawings: GalleryDrawing[];
  index: number;
  onUpdate: (d: GalleryDrawing) => void;
  hasMore: boolean;
  loadMore: () => Promise<{ drawings: GalleryDrawing[]; hasMore: boolean } | undefined>;
}

/** A drawing as an <img>: rasterised once, no DOM per stroke — cheap to scroll past. */
const Tile = memo(function Tile({
  drawing,
  onOpen,
  onVote,
}: {
  drawing: GalleryDrawing;
  onOpen: () => void;
  onVote: (action: Vote) => void;
}) {
  const src = useMemo(() => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(drawing.drawingSvg)}`, [drawing.drawingSvg]);
  const { t } = useTranslation();
  const liked = drawing.myVotes?.includes("like") ?? false;
  return (
    <li className="gb-tile">
      <div className="gb-tile__image">
      <button type="button" className="gb-tile__art" onClick={onOpen} aria-label={t("gallery_open", "Open drawing")}>
        <img src={src} alt="" decoding="async" draggable={false} />
      </button>
      <button
        type="button"
        className={`gb-like${liked ? " gb-like--on" : ""}`}
        onClick={() => onVote("like")}
        aria-label={t("gallery_like", "Like")}
        aria-pressed={liked}
      >
        {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        {drawing.likeCount > 0 && <span>{drawing.likeCount}</span>}
      </button>
      </div>
    </li>
  );
});

const QuestionCarousel = memo(function QuestionCarousel({ band, visitorId, reportedIds, onVote, onOpen }: {
  band: GalleryBand;
  visitorId: string;
  reportedIds: Set<number>;
  onVote: VoteFn;
  onOpen: (s: SpotlightState) => void;
}) {
  const { t } = useTranslation();
  const section = useRef<HTMLElement>(null);
  const track = useRef<HTMLUListElement>(null);
  const requested = useRef(0);
  const inFlight = useRef<ReturnType<SpotlightState["loadMore"]> | null>(null);
  const loaded = useRef<GalleryDrawing[]>([]);
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const [drawings, setDrawings] = useState<GalleryDrawing[]>([]);
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const [position, setPosition] = useState({ index: 0, start: true, end: false });
  const loadMore = useCallback(() => {
    if (inFlight.current) return inFlight.current;
    if (requested.current >= band.drawingIds.length) return Promise.resolve(undefined);
    setPending(true);
    setFailed(false);
    inFlight.current = (async () => {
      try {
        const ids = band.drawingIds.slice(requested.current, requested.current + DRAWINGS_PER_BATCH);
        const result = await CommunityApi.getGalleryDrawings(ids, visitorId);
        if (!result) { setFailed(true); return; }
        requested.current += ids.length;
        loaded.current = [...loaded.current, ...result];
        setDrawings(loaded.current);
        return { drawings: result, hasMore: requested.current < band.drawingIds.length };
      } finally {
        inFlight.current = null;
        setPending(false);
      }
    })();
    return inFlight.current;
  }, [band.drawingIds, visitorId]);

  useEffect(() => {
    const element = section.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { loadMore(); observer.disconnect(); }
    }, { rootMargin: "700px 0px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [loadMore]);
  const visible = drawings.filter((d) => !reportedIds.has(d.id));
  const measure = useCallback(() => {
    const row = track.current;
    if (!row) return;
    const step = (row.firstElementChild?.getBoundingClientRect().width ?? 1) + 8;
    setPosition({ index: Math.floor(row.scrollLeft / step), start: row.scrollLeft < 2, end: row.scrollLeft + row.clientWidth >= row.scrollWidth - 2 });
  }, []);
  useEffect(() => {
    measure();
    const row = track.current;
    if (!row) return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [visible.length, measure]);
  const onScroll = () => {
    measure();
    const row = track.current;
    if (row && row.scrollLeft + row.clientWidth >= row.scrollWidth - row.clientWidth) loadMore();
  };
  const advance = (direction: number) => {
    const row = track.current;
    if (!row) return;
    const step = (row.firstElementChild?.getBoundingClientRect().width ?? 100) + 8;
    row.scrollBy({ left: direction * step * 2, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };
  const update = useCallback((drawing: GalleryDrawing) => {
    loaded.current = loaded.current.map((d) => d.id === drawing.id ? drawing : d);
    setDrawings(loaded.current);
  }, []);
  const hasMore = requested.current < band.drawingIds.length;
  const open = (index: number) => {
    const delivered = new Set(visible.map((d) => d.id));
    onOpen({ band, drawings: visible, index, onUpdate: update, hasMore, loadMore: async () => {
      let next = loaded.current.filter((d) => !delivered.has(d.id));
      if (!next.length) {
        const page = await loadMore();
        if (!page) return;
        next = loaded.current.filter((d) => !delivered.has(d.id));
      }
      next.forEach((d) => delivered.add(d.id));
      return { drawings: next, hasMore: requested.current < band.drawingIds.length };
    } });
  };
  return <section ref={section} className="gb-carousel" aria-roledescription="carousel" aria-label={promptSegments(band.prompt, band.questionCardId).map((p) => p.text).join("")}>
    <header className="gb-carousel__heading"><h3><Prompt prompt={band.prompt} seed={band.questionCardId} /></h3></header>
    <ul ref={track} className="gb-track" onScroll={onScroll} tabIndex={0} aria-label={t("gallery_drawings", "Drawings")}
      onKeyDown={(event) => { if (event.target === event.currentTarget && ["ArrowLeft", "ArrowRight"].includes(event.key)) { event.preventDefault(); advance(event.key === "ArrowLeft" ? -1 : 1); } }}
      onPointerDown={(event) => {
        suppressClick.current = false;
        if (event.pointerType !== "mouse" || event.button !== 0) return;
        drag.current = { x: event.clientX, left: event.currentTarget.scrollLeft, moved: false };
      }}
      onPointerMove={(event) => {
        const state = drag.current;
        if (!state) return;
        if (!event.buttons) { drag.current = null; event.currentTarget.classList.remove("gb-track--dragging"); return; }
        if (Math.abs(event.clientX - state.x) > 5) {
          state.moved = true;
          suppressClick.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          event.currentTarget.classList.add("gb-track--dragging");
          event.currentTarget.scrollLeft = state.left - (event.clientX - state.x);
        }
      }}
      onPointerUp={(event) => {
        drag.current = null;
        event.currentTarget.classList.remove("gb-track--dragging");
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={(event) => { drag.current = null; event.currentTarget.classList.remove("gb-track--dragging"); }}
      onClickCapture={(event) => { if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); suppressClick.current = false; } }}>
      {visible.map((drawing, index) => <Tile key={drawing.id} drawing={drawing}
        onOpen={() => open(index)}
        onVote={async (action) => { const next = await onVote(drawing, action); if (next) update(next); }} />)}
      {!drawings.length && !failed && Array.from({ length: 3 }, (_, i) => <li key={i} className="gb-tile" aria-hidden><div className="gb-tile__image gb-tile--placeholder" /></li>)}
    </ul>
    <footer className="gb-carousel__footer">
      <span className="gb-carousel__position">{visible.length ? `${Math.min(position.index + 1, visible.length)} / ${hasMore ? band.drawingIds.length : visible.length}` : "—"}</span>
      <div className="gb-carousel__progress" aria-hidden><span style={{ width: `${position.end && !hasMore ? 100 : visible.length ? Math.min(100, ((position.index + 2.6) / (hasMore ? band.drawingIds.length : visible.length)) * 100) : 0}%` }} /></div>
      {failed && <button className="gb-retry" onClick={loadMore}>{t("gallery_retry", "Retry")}</button>}
      <button type="button" className="gb-carousel__arrow" onClick={() => advance(-1)} disabled={position.start} aria-label={t("gallery_previous", "Previous drawings")}><ChevronLeftIcon /></button>
      <button type="button" className="gb-carousel__arrow" onClick={() => { if (position.end && hasMore) loadMore(); else advance(1); }} disabled={position.end && (!hasMore || pending)} aria-label={t("gallery_next", "Next drawings")}><ChevronRightIcon /></button>
    </footer>
  </section>;
});

function Spotlight({ spot, onClose, onVote, onNavigate }: { spot: SpotlightState; onClose: () => void; onVote: (a: Vote) => void; onNavigate: (dir: number) => void }) {
  const { t } = useTranslation();
  const d = spot.drawings[spot.index];
  const touchX = useRef<number | null>(null);
  const imageSrc = useMemo(() => d ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(d.drawingSvg)}` : "", [d]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onNavigate(-1);
      if (e.key === "ArrowRight") onNavigate(1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, onNavigate]);

  if (!d) return null;
  const has = (a: string) => d.myVotes?.includes(a);

  return (
    <Dialog open onClose={onClose} aria-labelledby="gallery-spotlight-title" maxWidth={false} PaperProps={{ className: "gb-spot__shell" }} BackdropProps={{ className: "gb-spot__backdrop" }}>
      <div
        className="gb-spot__panel"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 50) onNavigate(dx < 0 ? 1 : -1);
          touchX.current = null;
        }}
      >
        <button type="button" className="gb-spot__close" onClick={onClose} aria-label={t("community_spotlight_close", "Close")}>
          <CloseIcon />
        </button>
        <h3 id="gallery-spotlight-title" className="gb-spot__prompt">
          <Prompt prompt={spot.band.prompt} seed={spot.band.questionCardId} />
        </h3>
        <div className="gb-spot__art" key={d.id}>
          <img src={imageSrc} alt={promptSegments(spot.band.prompt, spot.band.questionCardId).map((part) => part.text).join("")} draggable={false} />
        </div>
        <div className="gb-spot__bar">
          <button type="button" aria-label={t("gallery_like", "Like")} aria-pressed={!!has("like")} className={`gb-pill${has("like") ? " gb-pill--like" : ""}`} onClick={() => onVote("like")}>
            {has("like") ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            {d.likeCount || 0}
          </button>
          <button type="button" aria-label={t("gallery_dislike", "Dislike")} aria-pressed={!!has("dislike")} className={`gb-pill${has("dislike") ? " gb-pill--dislike" : ""}`} onClick={() => onVote("dislike")}>
            {has("dislike") ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
            {d.dislikeCount || 0}
          </button>
          <span className="gb-spot__pos">
            {spot.index + 1} / {spot.hasMore ? spot.band.drawingIds.length : spot.drawings.length}
          </span>
          <button type="button" className={`gb-icon${has("report") ? " gb-icon--on" : ""}`} onClick={() => onVote("report")} aria-label={t("gallery_report", "Report drawing")}>
            <FlagOutlinedIcon />
          </button>
        </div>
        {spot.index > 0 && (
          <button type="button" className="gb-spot__nav gb-spot__nav--prev" onClick={() => onNavigate(-1)} aria-label={t("gallery_previous", "Previous drawings")}>
            <ChevronLeftIcon />
          </button>
        )}
        {(spot.index < spot.drawings.length - 1 || spot.hasMore) && (
          <button type="button" className="gb-spot__nav gb-spot__nav--next" onClick={() => onNavigate(1)} aria-label={t("gallery_next", "Next drawings")}>
            <ChevronRightIcon />
          </button>
        )}
      </div>
    </Dialog>
  );
}

/**
 * A continuous image feed. Question metadata stays lightweight; drawings load
 * in small batches as they approach the viewport.
 */
export default function GalleryBands() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const visitorId = useMemo(getVisitorId, []);
  const [langs, setLangs] = useState<string[]>(() => {
    const fromUrl = new URLSearchParams(location.search).get("langs");
    const parsed = fromUrl?.split(",").filter((l) => LANGUAGE_IDS.has(l));
    return parsed && parsed.length ? parsed : defaultLanguages();
  });
  const [seed, setSeed] = useState(newSeed);
  const [bands, setBands] = useState<GalleryBand[]>([]);
  const [feedLimit, setFeedLimit] = useState(8);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [spin, setSpin] = useState(0);
  const [reportedIds, setReportedIds] = useState<Set<number>>(getReportedIds);
  const [spot, setSpot] = useState<SpotlightState | null>(null);
  const request = useRef(0);
  const sentinel = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const langKey = useMemo(() => [...langs].sort().join(","), [langs]);
  const hasMore = feedLimit < bands.length;

  const loadPage = useCallback(
    async () => {
      if (!langKey) return;
      const id = ++request.current;
      setLoading(true);
      const all: GalleryBand[] = [];
      let count = Infinity;
      while (all.length < count) {
        const page = await CommunityApi.getGalleryBands(langKey.split(","), seed, all.length, BANDS_PER_PAGE);
        if (id !== request.current) return;
        if (!page) { setLoading(false); return; }
        count = page.total;
        all.push(...page.bands);
        if (!page.bands.length) break;
      }
      setLoading(false);
      setTotal(count);
      setBands(all);
    },
    [langKey, seed]
  );

  // new languages or a shuffle: start again from the top
  useEffect(() => {
    ++request.current;
    setLoading(false);
    setBands([]);
    setTotal(langKey ? null : 0);
    setFeedLimit(8);
    loadPage();
    const params = new URLSearchParams(location.search);
    params.set("langs", langKey);
    history.replace({ search: params.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langKey, seed]);

  // Reveal the next batch of images before the reader reaches the bottom.
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && hasMore) setFeedLimit((n) => n + 8);
      },
      { rootMargin: "1200px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading, hasMore, feedLimit]);

  const vote: VoteFn = useCallback(
    async (d, action) => {
      let token: string | undefined;
      if (isAuthenticated && action === "report") {
        try {
          token = await getAccessTokenSilently();
        } catch {}
      }
      const res = await CommunityApi.voteGallery(d.id, visitorId, action, token);
      if (!res) return null;
      if (action === "report" && res.myVotes.includes("report")) {
        setReportedIds((prev) => {
          const next = new Set(Array.from(prev));
          next.add(d.id);
          localStorage.setItem("galleryReported", JSON.stringify(Array.from(next)));
          return next;
        });
        setSpot(null);
      }
      return { ...d, likeCount: res.likeCount, dislikeCount: res.dislikeCount, reportCount: res.reportCount, myVotes: res.myVotes };
    },
    [isAuthenticated, getAccessTokenSilently, visitorId]
  );

  const spotVote = async (action: Vote) => {
    if (!spot) return;
    const updated = await vote(spot.drawings[spot.index], action);
    if (!updated) return;
    spot.onUpdate(updated);
    setSpot((s) => (s ? { ...s, drawings: s.drawings.map((x) => (x.id === updated.id ? updated : x)) } : s));
  };
  const navigating = useRef(false);
  const navigate = useCallback(async (dir: number) => {
    if (!spot || navigating.current) return;
    if (dir < 0 || spot.index < spot.drawings.length - 1) {
      setSpot((s) => {
        if (!s) return s;
        const index = Math.max(0, Math.min(s.drawings.length - 1, s.index + dir));
        return { ...s, index, band: s.bandsByDrawing?.[s.drawings[index].id] ?? s.band };
      });
      return;
    }
    if (!spot.hasMore) return;
    navigating.current = true;
    try {
      const page = await spot.loadMore();
      if (!page) return;
      const next = page.drawings.filter((d) => !reportedIds.has(d.id));
      setSpot((s) => s === spot ? { ...s, drawings: [...s.drawings, ...next], hasMore: page.hasMore,
        index: next.length ? s.index + 1 : s.index } : s);
    } finally {
      navigating.current = false;
    }
  }, [spot, reportedIds]);
  const closeSpot = useCallback(() => setSpot(null), []);

  const toggleLang = (id: string) => setLangs((prev) => (prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]));
  const shuffle = () => {
    setSpin((s) => s + 1);
    setSeed(newSeed());
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="gb" ref={topRef}>
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }} role="group" aria-label={t("gallery_languages", "Languages")}>
          <Chip label={t("leaderboard_global")} variant={langs.length === LANGUAGE_LIST.length ? "filled" : "outlined"}
            color={langs.length === LANGUAGE_LIST.length ? "primary" : "default"}
            onClick={() => setLangs(LANGUAGE_LIST.map((lang) => lang.id))} />
          {LANGUAGE_LIST.map((lang) => <Chip key={lang.id} label={lang.name} aria-label={lang.label} aria-pressed={langs.includes(lang.id)}
            variant={langs.includes(lang.id) ? "filled" : "outlined"} color={langs.includes(lang.id) ? "primary" : "default"}
            onClick={() => toggleLang(lang.id)} />)}
        </Box>
      </Paper>

      <div className="gb-collection">
        <span>{t("gallery_collection", "Player drawings")}</span>
        <span className="gb-collection__actions"><span>{total !== null && total > 0 ? t("gallery_prompt_count", "{{count}} questions", { count: total }) : ""}</span>
          <button type="button" className="gb-shuffle" onClick={shuffle} disabled={!langKey}>
            <ShuffleIcon className="gb-shuffle__icon" style={{ transform: `rotate(${spin * 360}deg)` }} />{t("gallery_shuffle", "Shuffle")}
          </button></span>
      </div>
      {!langKey ? (
        <p className="gb-empty">{t("gallery_pick_language", "Pick a language to see its drawings")}</p>
      ) : total === 0 ? (
        <p className="gb-empty">{t("gallery_empty_language", "No drawings in these languages yet")}</p>
      ) : (
        <div className="gb-list">
          {bands.slice(0, feedLimit).map((band) => <QuestionCarousel key={`${seed}-${langKey}-${band.questionCardId}`} band={band} visitorId={visitorId} reportedIds={reportedIds} onVote={vote} onOpen={setSpot} />)}
          {loading && Array.from({ length: 4 }, (_, i) => <div key={i} className="gb-carousel" aria-hidden><div className="gb-skel--title" /><div className="gb-track">{Array.from({ length: 3 }, (_, j) => <div key={j} className="gb-tile"><div className="gb-tile__image gb-tile--placeholder" /></div>)}</div></div>)}
          <div ref={sentinel} className="gb-sentinel" />
        </div>
      )}

      {spot && <Spotlight spot={spot} onClose={closeSpot} onVote={spotVote} onNavigate={navigate} />}
    </div>
  );
}
